/**
 * Cliente para enviar correos vía Microsoft Graph desde el navegador
 * del usuario que dispara la acción (modo delegated).
 *
 * Estrategia de token:
 *   1. `acquireTokenSilent`: la mayoría de las veces el token ya está
 *      en cache de MSAL, no genera popup ni navegación.
 *   2. Si falla con `InteractionRequiredAuthError` (consent no dado,
 *      token expirado y refresh inválido), eleva a `acquireTokenPopup`.
 *      Es un popup, no un redirect, para no perder el contexto de la
 *      mutación en curso.
 *   3. Si MSAL no está disponible (backend dexie de desarrollo), tira
 *      `MailerNotConfiguredError` para que `notificarHandover` lo
 *      registre como `estado: "error"` y siga.
 *
 * El correo se envía vía POST `/me/sendMail`. Es delegated, así que
 * sale **desde el buzón del usuario logueado** — no hay buzón
 * compartido `pmis@tecpetrol.com` (eso requeriría modo application
 * y un backend que custodie el secret, lo cual descartamos).
 */

import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { getMsalInstance } from "@core/auth/msalInstance";
import { graphMailRequest } from "@core/auth/msalConfig";

export interface SendMailArgs {
  to: string[];
  cc?: string[];
  bcc?: string[];
  subject: string;
  /** Cuerpo HTML del correo. */
  html: string;
  /** Cuerpo en texto plano (fallback para clientes sin HTML). Opcional. */
  text?: string;
  /**
   * Si true, también guarda el correo en la carpeta "Elementos enviados"
   * del usuario. Default: true (lo dejamos como evidencia).
   */
  saveToSentItems?: boolean;
}

export class MailerNotConfiguredError extends Error {
  constructor() {
    super(
      "PMIS está corriendo en modo local (Dexie); no hay MSAL para enviar correos. " +
        "Despliega contra SharePoint (VITE_BACKEND=sharepoint + clientId/tenantId reales).",
    );
    this.name = "MailerNotConfiguredError";
  }
}

export class MailSendError extends Error {
  constructor(message: string, public statusCode?: number) {
    super(message);
    this.name = "MailSendError";
  }
}

/**
 * Adquiere un token Graph con scope `Mail.Send`, intentando primero
 * silenciosamente y elevando a popup si el consent no se ha dado.
 */
async function acquireMailToken(): Promise<string> {
  const msal = getMsalInstance();
  if (!msal) throw new MailerNotConfiguredError();

  const account = msal.getActiveAccount() ?? msal.getAllAccounts()[0];
  if (!account) {
    throw new MailSendError(
      "No hay sesión Microsoft activa; el usuario debe iniciar sesión antes de disparar acciones que envían correo.",
    );
  }

  try {
    const result = await msal.acquireTokenSilent({
      ...graphMailRequest,
      account,
    });
    return result.accessToken;
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) {
      // Consent no dado, o sesión necesita refresh interactivo.
      const result = await msal.acquireTokenPopup({
        ...graphMailRequest,
        account,
      });
      return result.accessToken;
    }
    throw err;
  }
}

/**
 * Envía un correo vía Microsoft Graph.
 *
 * @throws MailerNotConfiguredError si MSAL no está inicializado (modo local).
 * @throws MailSendError si Graph devuelve un error HTTP.
 */
export async function sendMail(args: SendMailArgs): Promise<void> {
  if (!args.to || args.to.length === 0) {
    throw new MailSendError("Lista de destinatarios vacía.");
  }
  const token = await acquireMailToken();

  const body = {
    message: {
      subject: args.subject,
      body: {
        contentType: "HTML" as const,
        content: args.html,
      },
      toRecipients: args.to.map((email) => ({ emailAddress: { address: email } })),
      ccRecipients:
        args.cc?.map((email) => ({ emailAddress: { address: email } })) ?? [],
      bccRecipients:
        args.bcc?.map((email) => ({ emailAddress: { address: email } })) ?? [],
    },
    saveToSentItems: args.saveToSentItems ?? true,
  };

  const resp = await fetch("https://graph.microsoft.com/v1.0/me/sendMail", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });

  // Graph devuelve 202 Accepted cuando el mensaje queda encolado.
  if (resp.status === 202) return;

  // Cualquier otro código es error.
  let errMsg = `Graph sendMail respondió ${resp.status}`;
  try {
    const data = (await resp.json()) as { error?: { message?: string } };
    if (data.error?.message) errMsg += `: ${data.error.message}`;
  } catch {
    // Cuerpo no era JSON; nos quedamos con el statusCode solo.
  }
  throw new MailSendError(errMsg, resp.status);
}

/**
 * Convierte HTML simple a texto plano (fallback). No es perfecto pero
 * cubre los templates del PMIS, que son muy estructurados (tablas y
 * párrafos). Útil para incluir como `body.contentType: "Text"` si en
 * algún momento queremos enviar texto en lugar de HTML.
 */
export function htmlAText(html: string): string {
  return html
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n\n")
    .replace(/<\/?[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}
